import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import { Autocomplete, Box, Chip, CircularProgress, Link, TextField, Tooltip, Typography } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { getAssignableTags, getTags, getInheritedTags, createTag } from '../redux/ducks/tag';
import { useHasPermission } from '../utils/permissionUtils';

const SEARCH_DELAY_MS = 300;
const SERVER_CAP = 500;

const OptionRow = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
});

const OptionLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
}));

const TagChip = styled(Chip)({
    maxWidth: '100%',
});

const ReadOnlyBox = styled(Box)({
    minWidth: 0,
});

const ReadOnlyLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
}));

const ChipRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
}));

const RetryLink = styled(Link)({
    verticalAlign: 'baseline',
});

const idOf = (value) => (value && typeof value === 'object' ? value._id : value) || null;

const sameId = (a, b) => {
    const left = idOf(a);
    const right = idOf(b);
    return Boolean(left && right && String(left) === String(right));
};

export const tagDisplayName = (tag, canSeeInternal) => {
    if (!tag) return '';
    if (!canSeeInternal) return tag.code;
    if (!tag.client) return `${tag.code} · General`;
    return tag.client.name ? `${tag.code} · ${tag.client.name}` : tag.code;
};

export const isInheritedTag = (entry, targetId) => Boolean(entry?.sourceId) && !sameId(entry.sourceId, targetId);

export const splitTagAssignments = (entries, targetId) => {
    const tags = [];
    const inheritedTags = [];
    (entries || []).forEach((entry) => {
        const tagId = idOf(entry?.tag);
        if (!tagId) return;
        (isInheritedTag(entry, targetId) ? inheritedTags : tags).push(tagId);
    });
    return { tags, inheritedTags };
};

export const tagSourceNames = (...entities) => Object.fromEntries(entities.filter((entity) => entity?._id && entity?.name).map((entity) => [String(entity._id), entity.name]));

const populatedEntries = (entries) => (entries || []).filter((entry) => entry?.tag?._id);

const entryKey = (entry) => `${idOf(entry.tag)}:${idOf(entry.sourceId) || ''}`;

const entriesKey = (entries) => populatedEntries(entries).map(entryKey).join(',');

const LEVEL_ORDER = ['Client', 'Account', 'Group', 'Project', 'Task'];

const levelRank = (entry, targetId) => {
    if (!isInheritedTag(entry, targetId)) return LEVEL_ORDER.length;
    const rank = LEVEL_ORDER.indexOf(entry.sourceType);
    return rank === -1 ? LEVEL_ORDER.length - 1 : rank;
};

const displayEntries = (entries, targetId) => {
    const byTag = new Map();
    populatedEntries(entries).forEach((entry) => {
        const tagId = String(idOf(entry.tag));
        const shown = byTag.get(tagId);
        if (!shown || (isInheritedTag(shown, targetId) && !isInheritedTag(entry, targetId))) byTag.set(tagId, entry);
    });
    return [...byTag.values()].sort((a, b) => levelRank(a, targetId) - levelRank(b, targetId));
};

export const useTagDraft = ({ enabled, targetType, targetId, parentType, parentId, tags, prefillInherited = false }) => {
    const dispatch = useDispatch();
    const [value, setValue] = useState([]);
    const [loading, setLoading] = useState(false);
    const [inheritedFailed, setInheritedFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [prefilled, setPrefilled] = useState(false);
    const tagsKey = entriesKey(tags);
    const tagsRef = useRef(tags);
    tagsRef.current = tags;
    const isEdit = Boolean(targetType && targetId);

    useEffect(() => {
        if (!enabled) {
            setValue([]);
            setLoading(false);
            setInheritedFailed(false);
            setPrefilled(false);
            return;
        }
        if (isEdit) setValue(populatedEntries(tagsRef.current));
    }, [enabled, isEdit, targetId, tagsKey]);

    useEffect(() => {
        if (!enabled) return undefined;
        const params = isEdit ? (prefillInherited ? { targetType, targetId } : null) : parentType && parentId ? { parentType, parentId } : null;
        setInheritedFailed(false);
        setPrefilled(false);
        if (!params) {
            setLoading(false);
            if (!isEdit) setValue((prev) => prev.filter((entry) => !isInheritedTag(entry, null)));
            return undefined;
        }
        let active = true;
        setLoading(true);
        dispatch(
            getInheritedTags(params, (data) => {
                if (!active) return;
                setLoading(false);
                if (!data) {
                    setInheritedFailed(true);
                    return;
                }
                const inherited = populatedEntries(data.tags);
                setValue((prev) => [...inherited, ...prev.filter((entry) => !isInheritedTag(entry, isEdit ? targetId : null))]);
                if (isEdit) setPrefilled(true);
            }),
        );
        return () => {
            active = false;
        };
    }, [dispatch, enabled, isEdit, prefillInherited, targetType, targetId, parentType, parentId, tagsKey, attempt]);

    const assignments = useMemo(() => {
        const split = splitTagAssignments(value, isEdit ? targetId : null);
        if (inheritedFailed) delete split.inheritedTags;
        return split;
    }, [value, isEdit, targetId, inheritedFailed]);

    return {
        value,
        setValue,
        loading,
        inheritedFailed,
        retry: () => setAttempt((count) => count + 1),
        assignments,
        dirty: prefilled || entriesKey(value) !== tagsKey,
    };
};

const filter = createFilterOptions({
    stringify: (tag) => [tag.code, tag.label, tag.client?.name].filter(Boolean).join(' '),
});

const LEVEL_NAMES = { Client: 'client', Account: 'account', Group: 'group', Project: 'project', Task: 'task' };

const chipLabel = (tag, canSeeInternal) => (tag.isDeleted ? `${tagDisplayName(tag, canSeeInternal)} (deleted)` : tagDisplayName(tag, canSeeInternal));

const chipTip = (entry, targetId, sourceNames) => {
    if (!isInheritedTag(entry, targetId)) return entry.tag.label || '';
    const level = LEVEL_NAMES[entry.sourceType] || 'a parent';
    const name = sourceNames?.[String(idOf(entry.sourceId))];
    return name ? `Inherited from ${level} “${name}”` : `Inherited from ${level}`;
};

export const TagChips = ({ value, targetId, label = 'Tags', lockedReason, sourceNames }) => {
    const hasPermission = useHasPermission();
    const canSeeInternal = hasPermission('canSeeInternal');
    const entries = displayEntries(value, targetId);
    const content = (
        <ReadOnlyBox>
            <ReadOnlyLabel>{label}</ReadOnlyLabel>
            <ChipRow>
                {entries.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No tags
                    </Typography>
                ) : (
                    entries.map((entry) => (
                        <Tooltip key={entryKey(entry)} title={chipTip(entry, targetId, sourceNames)}>
                            <TagChip size="small" color="primary" variant={isInheritedTag(entry, targetId) ? 'outlined' : 'filled'} label={chipLabel(entry.tag, canSeeInternal)} />
                        </Tooltip>
                    ))
                )}
            </ChipRow>
        </ReadOnlyBox>
    );
    return lockedReason ? (
        <Tooltip title={lockedReason} placement="top-start">
            {content}
        </Tooltip>
    ) : (
        content
    );
};

const TagPicker = ({
    draft,
    value: valueProp = [],
    onChange: onChangeProp,
    targetType,
    targetId,
    parentType,
    parentId,
    clientId,
    clientName,
    accountId,
    sourceNames,
    source = 'assignable',
    disabled = false,
    readOnly = false,
    allowCreate = true,
    lockedReason,
    label = 'Tags',
    placeholder = 'Add a tag',
    helperText,
    required = false,
    size,
}) => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const canSeeInternal = hasPermission('canSeeInternal');
    const value = draft ? draft.value : valueProp;
    const onChange = draft ? draft.setValue : onChangeProp;
    const [baseOptions, setBaseOptions] = useState([]);
    const [searchOptions, setSearchOptions] = useState(null);
    const [capped, setCapped] = useState(false);
    const [loadedKey, setLoadedKey] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [creating, setCreating] = useState(false);
    const requestRef = useRef(0);

    const scopeType = targetType && targetId ? targetType : parentType;
    const scopeId = targetType && targetId ? targetId : parentId;
    const entries = useMemo(() => populatedEntries(value), [value]);
    const shownEntries = useMemo(() => displayEntries(entries, targetId), [entries, targetId]);
    const listAll = source === 'all';
    const canCreate = allowCreate && !readOnly && (!canSeeInternal || clientId !== undefined);
    const allClient = clientId === null ? 'none' : clientId || undefined;
    const canLoad = Boolean(listAll || (scopeType && scopeId));
    const scopeKey = listAll ? `all:${allClient || ''}` : `${scopeType}:${scopeId}`;

    const fetchTags = (search, handleLoaded) => {
        const requestId = ++requestRef.current;
        setLoading(true);
        const done = (data) => {
            if (requestId !== requestRef.current) return;
            setLoading(false);
            handleLoaded(data);
        };
        dispatch(listAll ? getTags({ search, client: allClient, limit: SERVER_CAP }, done) : getAssignableTags({ targetType: scopeType, targetId: scopeId, search }, done));
    };

    useEffect(() => {
        setLoadedKey(null);
        setBaseOptions([]);
        setSearchOptions(null);
        setCapped(false);
        setLoadFailed(false);
        requestRef.current += 1;
        setLoading(false);
    }, [scopeKey]);

    const handleOpen = () => {
        if (readOnly || !canLoad || loading || loadedKey === scopeKey) return;
        fetchTags(undefined, (data) => {
            setLoadFailed(!data);
            if (!data) return;
            const tags = data.tags || [];
            setBaseOptions(tags);
            setCapped(tags.length >= SERVER_CAP || (data.total !== undefined && data.total > tags.length));
            setLoadedKey(scopeKey);
        });
    };

    const search = inputValue.trim();
    useEffect(() => {
        if (!capped || !search) {
            setSearchOptions(null);
            return undefined;
        }
        const timer = setTimeout(() => {
            fetchTags(search, (data) => setSearchOptions(data?.tags || []));
        }, SEARCH_DELAY_MS);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [capped, search, scopeKey]);

    const options = useMemo(() => {
        const loaded = searchOptions || baseOptions;
        const selected = shownEntries.map((entry) => entry.tag).filter((tag) => !loaded.some((option) => sameId(option, tag)));
        return [...selected, ...loaded];
    }, [shownEntries, searchOptions, baseOptions]);

    if (readOnly) return <TagChips value={entries} targetId={targetId} label={label} lockedReason={lockedReason} sourceNames={sourceNames} />;

    const withNames = (tag) => (tag?.client && !tag.client.name ? baseOptions.find((option) => sameId(option, tag)) || tag : tag);

    const toEntry = (tag) => ({ tag, sourceType: targetId ? targetType : null, sourceId: targetId || null });

    const handleCreate = (code) => {
        setCreating(true);
        const tagData = { code };
        if (canSeeInternal && clientId) tagData.client = clientId;
        const account = idOf(accountId) || (scopeType === 'Account' ? scopeId : null);
        if (account) tagData.account = account;
        dispatch(
            createTag(tagData, (data) => {
                setCreating(false);
                if (!data?.tag) return;
                setBaseOptions((prev) => [...prev, data.tag]);
                setInputValue('');
                onChange([...entries, toEntry(data.tag)]);
            }),
        );
    };

    const handleChange = (event, newValue) => {
        const pendingCreate = newValue.find((item) => item?.createCode);
        if (pendingCreate) {
            handleCreate(pendingCreate.createCode);
            return;
        }
        const next = newValue.map((item) => (item.tag ? item : toEntry(item)));
        const kept = entries.filter((entry) => next.some((item) => sameId(item.tag, entry.tag)));
        const added = next.filter((item) => !entries.some((entry) => sameId(entry.tag, item.tag)));
        onChange([...kept, ...added]);
    };

    const createOwner = canSeeInternal ? (clientId ? (clientName ? ` for ${clientName}` : '') : ' as General') : clientName ? ` for ${clientName}` : '';

    const filterOptions = (available, params) => {
        const filtered = filter(available, params);
        const code = params.inputValue.trim();
        if (!code || !canCreate || loading) return filtered;
        const createClient = canSeeInternal ? clientId || null : undefined;
        const exists = available.some((tag) => tag.code.toLowerCase() === code.toLowerCase() && (createClient === undefined || sameId(tag.client, createClient) || (!tag.client && !createClient)));
        return exists ? filtered : [...filtered, { createCode: code }];
    };

    const getOptionLabel = (option) => {
        if (option?.createCode) return option.createCode;
        return tagDisplayName(option?.tag || option, canSeeInternal);
    };

    const renderOption = (props, option) => {
        const { key, ...rest } = props;
        if (option.createCode) {
            return (
                <li key={key} {...rest}>
                    Create tag “{option.createCode}”{createOwner}
                </li>
            );
        }
        return (
            <li key={key} {...rest}>
                <OptionRow>
                    <Typography variant="body2">{tagDisplayName(option, canSeeInternal)}</Typography>
                    {option.label && <OptionLabel>{option.label}</OptionLabel>}
                </OptionRow>
            </li>
        );
    };

    const renderTags = (selected, getTagProps) =>
        selected.map((entry, index) => {
            const { key, ...chipProps } = getTagProps({ index });
            return (
                <Tooltip key={entryKey(entry)} title={chipTip(entry, targetId, sourceNames)}>
                    <TagChip
                        {...chipProps}
                        size="small"
                        color={entry.tag.isDeleted ? 'default' : 'primary'}
                        variant={isInheritedTag(entry, targetId) || entry.tag.isDeleted ? 'outlined' : 'filled'}
                        label={chipLabel(withNames(entry.tag), canSeeInternal)}
                    />
                </Tooltip>
            );
        });

    const inheritedFailed = Boolean(draft?.inheritedFailed);
    const helper = inheritedFailed ? (
        <>
            Couldn’t load inherited tags, so they’ll be applied unchanged.{' '}
            <RetryLink component="button" type="button" variant="caption" onClick={draft.retry}>
                Retry
            </RetryLink>
        </>
    ) : (
        helperText
    );

    return (
        <Autocomplete
            multiple
            size={size}
            filterSelectedOptions
            value={shownEntries}
            options={options}
            onOpen={handleOpen}
            onChange={handleChange}
            inputValue={inputValue}
            onInputChange={(event, next) => setInputValue(next)}
            filterOptions={filterOptions}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(option, entry) => sameId(option, entry?.tag || entry)}
            renderOption={renderOption}
            renderTags={renderTags}
            disabled={disabled || creating}
            loading={loading}
            loadingText="Loading tags…"
            noOptionsText={loadFailed ? 'Tags couldn’t be loaded' : 'No matching tags'}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={disabled ? undefined : placeholder}
                    required={required}
                    error={inheritedFailed}
                    helperText={helper}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {(loading || creating || draft?.loading) && <CircularProgress color="inherit" size={18} />}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};

export default TagPicker;
